import React from 'react';
import { ClickAwayListener, InformationIcon, Tooltip } from '@cin7/ui';
import PriceDisplay from '../../Global/PriceDisplay';

import './Styles/_ProductBulkPricing.scss';

export interface BulkPricingTier {
    minQuantity: number;
    isBaseUnit: boolean;
    packPrice: number;
    unitPrice: number;
}

export interface BulkPricing {
    fromUnitPrice: number;
    tiers: BulkPricingTier[];
}

interface ProductBulkPricingProps {
    bulkPricing?: BulkPricing | null;
}

export const ProductBulkPricing = (props: ProductBulkPricingProps) => {
    const { bulkPricing } = props;

    const [open, setOpen] = React.useState(false);

    // The service only sends this for products with a ladder to show.
    if (!bulkPricing || !bulkPricing.tiers || bulkPricing.tiers.length === 0) {
        return null;
    }

    // The base unit's price already is the unit price, so no per-unit figure.
    const ladder = (
        <div className="bulk-pricing-ladder">
            <div className="bulk-pricing-ladder-title">Bulk pricing</div>
            <table>
                <thead>
                    <tr>
                        <th>Pack size</th>
                        <th>Price</th>
                        <th>Per unit</th>
                    </tr>
                </thead>
                <tbody>
                    {bulkPricing.tiers.map((tier, index) => (
                        <tr className="bulk-pricing-ladder-row" key={index}>
                            <td>{tier.isBaseUnit ? "Each" : tier.minQuantity}</td>
                            <td><PriceDisplay value={tier.packPrice} /></td>
                            <td>{tier.isBaseUnit ? "—" : <PriceDisplay value={tier.unitPrice} />}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // Hover opens it for a mouse only - a tap fires emulated mouse events too, and it's the click
    // that opens it there. A tap anywhere else closes it, so only one tile's ladder is ever open.
    const handlePointerEnter = (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse') {
            setOpen(true);
        }
    };

    const handlePointerLeave = (e: React.PointerEvent) => {
        if (e.pointerType === 'mouse') {
            setOpen(false);
        }
    };

    return (
        <span className="product-bulk-pricing">
            <span className="product-bulk-pricing-label">
                Bulk from <PriceDisplay value={bulkPricing.fromUnitPrice} /> / unit
            </span>
            <ClickAwayListener onClickAway={() => setOpen(false)}>
                {/* The tile opens the product on click, which the icon and the ladder's own clicks (it
                    portals, but still bubbles through React) shouldn't do - the label still does. */}
                <span className="product-bulk-pricing-trigger" onClick={(e) => e.stopPropagation()}>
                    <Tooltip
                        open={open}
                        title={ladder}
                        placement="top"
                        arrow
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                        classes={{ tooltip: 'bulk-pricing-tooltip', arrow: 'bulk-pricing-tooltip-arrow' }}
                    >
                        <button
                            type="button"
                            className="product-bulk-pricing-info"
                            aria-label="Bulk pricing"
                            aria-expanded={open}
                            onPointerEnter={handlePointerEnter}
                            onPointerLeave={handlePointerLeave}
                            onClick={() => setOpen(true)}
                            onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
                        >
                            <InformationIcon />
                        </button>
                    </Tooltip>
                </span>
            </ClickAwayListener>
        </span>
    );
};

export default ProductBulkPricing;
